import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Building2, HandHeart, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const TIER_ICONS: Record<string, any> = {
  community: HandHeart,
  catalyst: Building2,
  anchor: Trophy,
};

export default function Sponsorships() {
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [busy, setBusy] = useState(false);

  const { data: tiers } = useQuery({
    queryKey: ["sponsor-tiers"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("sponsor_tiers")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: activeCount } = useQuery({
    queryKey: ["sponsors-active-count"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("sponsors")
        .select("tier", { count: "exact", head: false })
        .eq("is_active", true);
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach((s: any) => {
        const t = String(s.tier || "").toLowerCase();
        counts[t] = (counts[t] || 0) + 1;
      });
      return counts;
    },
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Please add your name and email");
      return;
    }
    setBusy(true);
    const { error } = await (supabase as any).from("signups").insert({
      email: form.email,
      name: form.name,
      organization: form.org,
      source: "sponsor_inquiry",
    });
    setBusy(false);
    if (error) {
      toast.error("Couldn't submit", { description: error.message });
      return;
    }
    toast.success("Thanks — we'll be in touch within 2 business days.");
    setForm({ name: "", email: "", org: "", message: "" });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-5xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-12">
          <Sparkles className="h-8 w-8 mx-auto text-primary mb-3" />
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-3">
            Sponsor the civic ecosystem
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Align your brand with the work of advocates, agencies, funders, and policymakers across Alabama.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-12">
          {tiers?.map((t: any) => {
            const Icon = TIER_ICONS[t.slug] ?? Sparkles;
            const used = activeCount?.[t.slug] ?? 0;
            const remaining = Math.max((t.inventory_total ?? 0) - used, 0);
            const placements: string[] = Array.isArray(t.placements) ? t.placements : [];
            return (
              <div key={t.slug} className="rounded-2xl border border-border bg-card p-6 flex flex-col">
                <Icon className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-display text-xl font-semibold text-foreground">{t.name}</h3>
                <div className="text-2xl font-bold text-foreground mt-1">
                  ${(t.monthly_cents / 100).toLocaleString()}
                  <span className="text-sm font-normal text-muted-foreground">/mo</span>
                </div>
                {t.blurb && <p className="text-sm text-muted-foreground mt-2 mb-4">{t.blurb}</p>}
                <ul className="space-y-2 text-sm text-foreground/80 flex-1">
                  {placements.map((p) => (
                    <li key={p}>• {p}</li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
                  <span>
                    <span className="text-foreground font-medium">{remaining}</span> of {t.inventory_total} slots open
                  </span>
                  {t.impression_cap && (
                    <span>Up to {t.impression_cap.toLocaleString()} impressions/mo</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 md:p-8 max-w-xl mx-auto space-y-3">
          <h2 className="font-display text-2xl font-semibold text-foreground">Become a sponsor</h2>
          <p className="text-sm text-muted-foreground">Tell us about your organization.</p>
          <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input placeholder="Organization" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} />
          <Textarea placeholder="What are you hoping to achieve?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "Sending…" : "Request sponsorship info"}
          </Button>
        </form>
      </div>
    </div>
  );
}